import React, { useEffect } from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { createUser, updateUser } from "../redux/actions/userActions";
import { getBranches } from "../redux/actions/branchActions";
import { getRoles } from "../redux/actions/roleActions";
import { cleanFormData } from "../helpers/formHelpers";
import { userActions } from "../redux/slices/user";
import { UserStatus } from "../shared/enums/UserStatus";
import { UserRequestDto } from "../shared/dtos/user/UserRequestDto";
import { UserResponseDto } from "../shared/dtos/user/UserResponseDto";
import { UserSummaryDto } from "../shared/dtos/user/UserSummaryDto";

const createUserSchema = Yup.object().shape({
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
  username: Yup.string().required("Required"),
  email: Yup.string().email("Invalid email").nullable(),
  branchId: Yup.string().required("Required"),
  userRole: Yup.string().required("Required"),
  password: Yup.string().min(6, "Min 6 characters").required("Required"),
});

const updateUserSchema = Yup.object().shape({
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
  username: Yup.string().required("Required"),
  email: Yup.string().email("Invalid email").nullable(),
  branchId: Yup.string().required("Required"),
  userRole: Yup.string().required("Required"),
  status: Yup.string().oneOf(Object.values(UserStatus)).required("Required"),
  password: Yup.string().min(6, "Min 6 characters").nullable(),
});

interface CreateOrUpdateUserFormProps {
  user?: UserResponseDto | UserSummaryDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const CreateOrUpdateUserForm: React.FC<CreateOrUpdateUserFormProps> = ({
  user = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.auth);
  const { items: branches } = useAppSelector((state) => state.branch);
  const { items: roles } = useAppSelector((state) => state.role);

  const isSuperAdmin = userInfo?.userRole === "SUPER_ADMIN";

  useEffect(() => {
    if (!roles?.length) dispatch(getRoles());
    if (isSuperAdmin && !branches?.length) dispatch(getBranches());
  }, [branches?.length, dispatch, isSuperAdmin, roles?.length]);

  const initialValues: Partial<UserRequestDto> = {
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    branchId: user?.branchId || (isSuperAdmin ? "" : userInfo?.branchId) || "",
    userRole: user?.userRole || "",
    username: user?.username || "",
    status: user?.status || UserStatus.ACTIVE,
    password: "",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(
            isEditing
              ? userActions.setErrorUpdate(null)
              : userActions.setErrorCreate(null),
          )
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={isEditing ? updateUserSchema : createUserSchema}
        onSubmit={(values) => {
          const payload = cleanFormData(values) as unknown as UserRequestDto;
          if (isEditing && user) {
            if (!values.password) delete payload.password;
            dispatch(updateUser({ id: user.id, values: payload }));
          } else {
            dispatch(createUser(payload));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<Partial<UserRequestDto>>) => (
          <Form>
            <Stack spacing={3}>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: isSuperAdmin ? "1fr 1fr" : "1fr",
                  },
                }}
              >
                <FormSelectField
                  name="userRole"
                  label="User Role"
                  options={roles.map((r) => ({ value: r.name, label: r.name }))}
                />
                {isSuperAdmin && (
                  <FormSelectField
                    name="branchId"
                    label="Assign to Branch"
                    options={branches.map((b) => ({
                      value: b.id,
                      label: b.name,
                    }))}
                  />
                )}
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                }}
              >
                <FormTextField name="firstName" label="First Name" />
                <FormTextField name="lastName" label="Last Name" />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                }}
              >
                <FormTextField name="username" label="Username" />
                <FormTextField
                  name="email"
                  label="Email Address"
                  type="email"
                  disabled={isEditing}
                />
                {isEditing && (
                  <FormSelectField
                    name="status"
                    label="Account Status"
                    options={Object.values(UserStatus).map((s) => ({
                      value: s,
                      label: s,
                    }))}
                  />
                )}
              </Box>

              <FormTextField
                name="password"
                label={isEditing ? "Change Password (Optional)" : "Password"}
                type="password"
              />

              <SubmitButton
                title={isEditing ? "Save Changes" : "Register User"}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <PersonAddOutlinedIcon />
                }
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdateUserForm;
